<template>
  <define-table>
    <pf-table compact striped>
      <pf-thead>
        <pf-tr>
          <pf-th>{{ $t('Codice') }}</pf-th>
          <pf-th>{{ $t('Descrizione') }}</pf-th>
          <pf-th class="pf-v6-u-text-align-end">{{ $t('Listino') }}</pf-th>
          <pf-th class="pf-v6-u-text-align-end">{{ $t('Sconto') }}</pf-th>
          <pf-th class="pf-v6-u-text-align-end">{{ $t('Importo') }}</pf-th>
        </pf-tr>
      </pf-thead>
      <pf-tbody>
        <pf-tr
          v-for="prodotto in products"
          :key="prodotto.id"
          :class="{ 'striked': prodotto.disdire }"
        >
          <pf-td :data-label="$t('Codice')">{{ prodotto.codice }}</pf-td>
          <pf-td :data-label="$t('Descrizione')" class="text-preline">
            <template v-if="prodotto.qt != 1">{{ prodotto.qt }} x </template>
            {{ prodotto.descrizione }}
          </pf-td>
          <pf-td :data-label="$t('Listino')" class="pf-v6-u-text-align-end">{{ $currency(prodotto.importo, currency) }}</pf-td>
          <pf-td :data-label="$t('Sconto')" class="pf-v6-u-text-align-end">{{ $currency(prodotto.sconto) }} %</pf-td>
          <pf-td :data-label="$t('Importo')" class="pf-v6-u-text-align-end" >{{ $currency(prodotto.imponibile, currency) }}</pf-td>
        </pf-tr>
        <template v-if="domain && !hideGroupInfo">
          <pf-tr v-if="importoTotaleAnnuale > 0">
            <pf-td colspan="4" class="pf-v6-u-text-align-end">
              <strong>{{ $t('Importo parziale annuale gruppo:') }}</strong>
            </pf-td>
            <pf-td class="pf-v6-u-text-align-end">
              <strong>{{ $currency(importoTotaleAnnuale, currency) }}</strong>
            </pf-td>
          </pf-tr>
          <pf-tr v-if="importoTotaleMensile > 0">
            <pf-td colspan="4" class="pf-v6-u-text-align-end">
              <strong>{{ $t('Importo parziale mensile gruppo:') }}</strong>
            </pf-td>
            <pf-td class="pf-v6-u-text-align-end">
              <strong>{{ $currency(importoTotaleMensile, currency) }}</strong>
            </pf-td>
          </pf-tr>
          <pf-tr v-if="importoTotaleUnatantum > 0">
            <pf-td colspan="4" class="pf-v6-u-text-align-end">
              <strong>{{ $t('Importo parziale una tantum gruppo:') }}</strong>
            </pf-td>
            <pf-td class="pf-v6-u-text-align-end">
              <strong>{{ $currency(importoTotaleUnatantum, currency) }}</strong>
            </pf-td>
          </pf-tr>
        </template>
      </pf-tbody>
    </pf-table>
  </define-table>

  <pf-card v-if="!noCard" v-bind="$attrs">
    <pf-card-header>
      <pf-flex justify-content="space-between" style="width: 100%;">
        <pf-flex-item align-self="flex-start">
          <pf-title v-if="domain" h="5">{{ $t('Prodotti associati al dominio {0}', [domain]) }}</pf-title>
          <pf-title v-else h="5">{{ $t('Altri prodotti') }}</pf-title>
        </pf-flex-item>
        <pf-flex-item v-if="onRemoveProducts && domain" align-self="flex-end">
          <pf-button variant="danger" @click="onRemoveProducts(domain)" small>
            <template #icon>
              <xmark-icon />
            </template>
            {{ $t("Rimuovi") }}
          </pf-button>
        </pf-flex-item>
      </pf-flex>
    </pf-card-header>

    <pf-card-body>
      <reuse-table />
    </pf-card-body>
  </pf-card>

  <div v-else v-bind="$attrs">
    <pf-title v-if="domain" h="5">{{ $t('Prodotti associati al dominio {0}', [domain]) }}</pf-title>
    <pf-title v-else h="5">{{ $t('Altri prodotti') }}</pf-title>

    <div class="pf-v6-u-mt-md">
      <reuse-table />
    </div>
  </div>
</template>

<style scoped>
.striked {
  text-decoration: line-through;
}
.text-preline {
  white-space: pre-line;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue';
import type { OfferProductDetail } from '@/resources';
import { XmarkIcon } from '@vue-patternfly/icons/xmark-icon';
import { createReusableTemplate } from '@vueuse/core';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  products: OfferProductDetail[],
  domain?: string,
  currency: 'CHF'|'EUR',
  noCard?: boolean,
  onRemoveProducts?: (domain: string) => void;
}>();

const [DefineTable, ReuseTable] = createReusableTemplate();

const importoTotaleAnnuale = computed(() => props.products.filter(p => !p.disdire).reduce((sum, product) => sum + (product.durata === 'Annuale' ? product.imponibile : 0), 0));
const importoTotaleMensile = computed(() => props.products.filter(p => !p.disdire).reduce((sum, product) => sum + (product.durata === 'Mensile' ? product.imponibile : 0), 0));
const importoTotaleUnatantum = computed(() => props.products.filter(p => !p.disdire).reduce((sum, product) => sum + (product.durata === 'Una tantum' ? product.imponibile : 0), 0));

const hideGroupInfo = computed(() => props.products.length === 1 && !props.onRemoveProducts);
</script>
